import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mergeClasses } from '@fluentui/react-components';
import type { RoomDetail, HealthStatus } from '@/types/reports';
import { useRoomHealthKpiCardsStyles } from './RoomHealthKpiCards.styles';

export interface RoomHealthKpiCardsProps {
  data: RoomDetail[];
}

function getHealthStatus(utilization: number): HealthStatus {
  if (utilization >= 80) return 'constrained';
  if (utilization >= 40) return 'healthy';
  return 'underutilized';
}

export const RoomHealthKpiCards = ({ data }: RoomHealthKpiCardsProps) => {
  const styles = useRoomHealthKpiCardsStyles();
  const { t } = useTranslation('reports');

  const counts = useMemo(() => {
    const result = { underutilized: 0, healthy: 0, constrained: 0 };
    for (const room of data) {
      result[getHealthStatus(room.utilization)]++;
    }
    return result;
  }, [data]);

  const cards: { status: HealthStatus; labelKey: string; subtitleKey: string; accentClass: string; valueClass: string }[] = [
    { status: 'underutilized', labelKey: 'kpi.roomsUnderutilized', subtitleKey: '< 40% utilization', accentClass: styles.accentAmber, valueClass: styles.valueAmber },
    { status: 'healthy', labelKey: 'kpi.roomsHealthy', subtitleKey: '40–80% utilization', accentClass: styles.accentGreen, valueClass: styles.valueGreen },
    { status: 'constrained', labelKey: 'kpi.roomsConstrained', subtitleKey: '≥ 80% utilization', accentClass: styles.accentRed, valueClass: styles.valueRed },
  ];

  return (
    <div className={styles.root}>
      {cards.map((card) => (
        <section
          key={card.status}
          className={mergeClasses(styles.card, card.accentClass)}
          aria-label={t(card.labelKey)}
        >
          <span className={mergeClasses(styles.value, card.valueClass)}>
            {counts[card.status]}
          </span>
          <span className={styles.label}>{t(card.labelKey)}</span>
          <span className={styles.subtitle}>{card.subtitleKey}</span>
        </section>
      ))}
    </div>
  );
};

RoomHealthKpiCards.displayName = 'RoomHealthKpiCards';
